/**
 * materializePrefill — mapper PUR `form_response.data + version.schema` →
 * `Partial<MaterializeDraftData>` par `field.role` (F2-Forms-1e-B).
 *
 * Best-effort typé : seul un rôle porté par un champ de type compatible est
 * mappé ; les valeurs vides/invalides/hors-borne sont **abandonnées
 * silencieusement** (jamais bloquantes). Aucun réseau, aucune dépendance React.
 * Ne pré-remplit JAMAIS la sélection d'une compagnie/projet existant (seulement
 * les champs de création + `applyCompanyData`). `insurer_codes` hors scope v1.
 *
 * Les valeurs pré-remplies restent **éditables** dans le wizard ; la
 * matérialisation reste soumise à revue humaine (aucun auto-apply).
 */
import { Schemas } from "@kollectivia/shared";

import type { MaterializeDraftData } from "../../../../lib/materializeDraftStorage";

type RawField = { key: string; type: string; role?: string };
type RawSchema = { sections?: Array<{ fields?: RawField[] }> };

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_STR = 200;

function nonEmptyString(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}
function boundedString(v: unknown): string | null {
  const s = nonEmptyString(v);
  return s !== null && s.length <= MAX_STR ? s : null;
}
function boundedInt(v: unknown): number | null {
  return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 1_000_000 ? v : null;
}
function isoDate(v: unknown): string | null {
  return typeof v === "string" && DATE_RE.test(v) ? v : null;
}

export function buildMaterializePrefill(
  responseData: Record<string, unknown>,
  versionSchema: unknown,
): Partial<MaterializeDraftData> {
  const sections = (versionSchema as RawSchema).sections ?? [];
  const newProject: NonNullable<MaterializeDraftData["newProject"]> = {};
  const newCompany: NonNullable<MaterializeDraftData["newCompany"]> = {};
  const applyCompanyData: NonNullable<MaterializeDraftData["applyCompanyData"]> = {};
  // Les dates de projet ne transitent PAS par `new_project` (buildBody ne pose
  // que title/insurer/guarantee/tags) mais par `review_snapshot.apply_project_data`
  // — cf. MaterializeWizard.buildBody. On cible donc `applyProjectData` pour
  // qu'elles atteignent réellement le projet créé.
  const applyProjectData: NonNullable<MaterializeDraftData["applyProjectData"]> = {};

  for (const section of sections) {
    for (const f of section.fields ?? []) {
      const role = f.role;
      if (!role || !(Schemas.FORM_FIELD_ROLES as readonly string[]).includes(role)) continue;
      // best-effort : ignore un rôle porté par un type incompatible.
      if (!Schemas.roleAllowsType(role as Schemas.FormFieldRole, f.type as Schemas.FormFieldType)) {
        continue;
      }
      const v = responseData[f.key];
      switch (role as Schemas.FormFieldRole) {
        case "project_title": {
          const s = boundedString(v);
          if (s !== null) newProject.title = s;
          break;
        }
        case "company_legal_name": {
          const s = boundedString(v);
          if (s !== null) newCompany.legal_name = s;
          break;
        }
        case "employee_count": {
          const n = boundedInt(v);
          if (n !== null) applyCompanyData.employee_count = n;
          break;
        }
        case "effect_date": {
          const d = isoDate(v);
          if (d !== null) applyProjectData.effect_date = d;
          break;
        }
        case "renewal_target_date": {
          const d = isoDate(v);
          if (d !== null) applyProjectData.renewal_target_date = d;
          break;
        }
        case "deadline_date": {
          const d = isoDate(v);
          if (d !== null) applyProjectData.deadline_date = d;
          break;
        }
      }
    }
  }

  const prefill: Partial<MaterializeDraftData> = {};
  if (Object.keys(newProject).length > 0) prefill.newProject = newProject;
  if (Object.keys(newCompany).length > 0) prefill.newCompany = newCompany;
  if (Object.keys(applyCompanyData).length > 0) prefill.applyCompanyData = applyCompanyData;
  if (Object.keys(applyProjectData).length > 0) prefill.applyProjectData = applyProjectData;
  return prefill;
}

/** True si le prefill contient au moins une valeur (pour l'avertissement UX). */
export function hasPrefill(prefill: Partial<MaterializeDraftData>): boolean {
  return Object.keys(prefill).length > 0;
}
